import Tooltip from "bootstrap/js/dist/tooltip";
import profanity from "config/profanity";
import { FocusError } from "focus-formik-error";
import { useFormik } from "formik";
import moment from "moment";
import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import Flatpickr from "react-flatpickr";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { patchPhysicianAppointment, postPhysicianAppointment } from "services/api/taptype/appointments";
import { getUsers } from "services/api/taptype/user";
import { userSelector } from "store/auth";
import { globalsSelector } from "store/globals";
import { segmentsSelector } from "store/segments";
import {
	apiHandler,
	calculateAgeFromBirthDate,
	emailRegExp,
	flattenDeep,
	fromApiDate,
	getLaboratoryAndImagingSegmentFromSegments,
	getMedicalSegmentFromSegments,
	getPharmacySegmentFromSegments,
	isDiagnosticCategory,
	isFunction,
	isLabCategory,
	isWellnessCategory,
	phoneRegExp,
	ssnRegExp,
	toApiDate,
	urlRegExp,
} from "utils/helpers";
import vars from "utils/vars";
import CasesSearchInput from "views/components/CasesSearchInput";
import DocumentsSearchInput from "views/components/DocumentsSearchInput";
import { array, number, object, string } from "yup";

const Appointment = ({
	appointment,
	patient = {},
	isSinglePatientView,
	onSuccess,
	onTimeOverlapError,
	forceTimeOverlap,
	onChangePatientClickHandler,
	onUploadNewDocumentClickHandler,
	selectedDocuments,
	setSelectedDocuments,
	refetchDocuments,
	setRefetchDocuments,
	isEdit,
	onDeleteButtonClickHandler,
}) => {
	const { t } = useTranslation();

	// ref hook
	const usersCancelRequestRef = useRef(null);
	const postAppointmentCancelRequestRef = useRef(null);
	const formRef = useRef(null);

	// redux global state
	const globals = useSelector(globalsSelector);
	const segments = useSelector(segmentsSelector);
	const user = useSelector(userSelector);

	// state hooks
	const [isShareQuestionerInputShownState, setIsShareQuestionerInputShownState] = useState(
		(isEdit && appointment?.case_id) || false
	);
	const [usersState, setUsersState] = useState([]);
	const [usersLoadingState, setUsersLoadingState] = useState(false);
	const [isAppointmentLoadingState, setIsAppointmentLoadingState] = useState(false);

	// constants
	const uniqueId = useId();
	const medicalSegment = useMemo(() => getMedicalSegmentFromSegments(segments), [segments]);
	const pharmacySegment = useMemo(() => getPharmacySegmentFromSegments(segments), [segments]);
	const laboratoryAndImagingSegment = useMemo(() => getLaboratoryAndImagingSegmentFromSegments(segments), [segments]);
	const isCurrentSegmentMedicalSegment = medicalSegment?.id === globals?.segment?.id || false;
	const isCurrentSegmentPharmacySegment = pharmacySegment?.id === globals?.segment?.id || false;
	const isCurrentSegmentLaboratoryAndImagingSegment =
		laboratoryAndImagingSegment?.id === globals?.segment?.id || false;
	const isCurrentCategoryLabCategory = useMemo(() => isLabCategory(user?.segment_category), [user?.segment_category]);
	const isCurrentCategoryDiagnosticCategory = useMemo(
		() => isDiagnosticCategory(user?.segment_category),
		[user?.segment_category]
	);
	const isCurrentCategoryWellnessCategory = useMemo(
		() => isWellnessCategory(user?.segment_category),
		[user?.segment_category]
	);
	const patientGender = patient?.gender || "";
	const patientAge = {
		...((patient?.date_of_birth && calculateAgeFromBirthDate(patient?.date_of_birth)) || {}),
	};
	const defaultUserSlotDuration = user?.slot_duration || 10;

	// Determine default appointment type based on segment
	const defaultAppointmentType = useMemo(() => {
		if (isCurrentSegmentMedicalSegment) {
			return "doctor_visit";
		} else if (isCurrentSegmentPharmacySegment) {
			return "pharmacy_assessment";
		}
		return "";
	}, [isCurrentSegmentMedicalSegment, isCurrentSegmentPharmacySegment]);

	const APPOINTMENT_TYPES = useMemo(
		() => [
			...(isCurrentSegmentMedicalSegment
				? [
						{ label: "Doctor Visit", value: "doctor_visit" },
						{ label: "Referral", value: "referral" },
				  ]
				: []),
			...(isCurrentSegmentPharmacySegment
				? [{ label: "Pharmacy Assessment", value: "pharmacy_assessment" }]
				: []),
			...(isCurrentSegmentLaboratoryAndImagingSegment && isCurrentCategoryDiagnosticCategory
				? [{ label: "Diagnostic Imaging", value: "diagnostic_imaging" }]
				: []),
			...(isCurrentSegmentLaboratoryAndImagingSegment && isCurrentCategoryLabCategory
				? [{ label: "Lab Tests", value: "lab_test" }]
				: []),
			...(isCurrentSegmentLaboratoryAndImagingSegment && isCurrentCategoryWellnessCategory
				? [{ label: "Wellness Checkup/Follow-up", value: "wellness_check" }]
				: []),
			{ label: "Other", value: "other" },
		],
		[
			isCurrentSegmentMedicalSegment,
			isCurrentSegmentPharmacySegment,
			isCurrentSegmentLaboratoryAndImagingSegment,
			isCurrentCategoryDiagnosticCategory,
			isCurrentCategoryLabCategory,
			isCurrentCategoryWellnessCategory,
		]
	);

	// event handlers
	const onAppointmentFormikSubmitHandler = ({ patient_name: _patient_name, ...body }, { resetForm, setErrors }) => {
		// Aborting an in-flight schedule request does not undo the appointment the API is
		// already creating for it, so a second click used to add a second appointment
		// (and a third, a third) while the UI only ever showed the last response.
		if (isAppointmentLoadingState) return;

		setIsAppointmentLoadingState(true);
		postAppointmentCancelRequestRef.current = new AbortController();

		// The picker holds yyyy-mm-dd; the endpoint validates d-m-Y. The overlap callback below
		// hands this same body to the "proceed anyway" resubmit, so convert once, here.
		const requestBody = { ...body, date: toApiDate(body?.date) };

		apiHandler(isEdit ? patchPhysicianAppointment : postPhysicianAppointment, {
			data: {
				...(isEdit ? { params: { appointment_id: appointment?.id } } : {}),
				body: { ...requestBody, ...(forceTimeOverlap ? { force_submit: true } : {}) },
				options: {
					signal: postAppointmentCancelRequestRef.current.signal,
					headers: { "x-prevent-error-flashes": true },
				},
			},
			onError: ({ errors, data: { is_overlapping = false } = {}, message = "" }) => {
				if (errors) setErrors(errors);
				if (is_overlapping && isFunction(onTimeOverlapError)) onTimeOverlapError(message, requestBody);
				setIsAppointmentLoadingState(false);
			},
			onSuccess: () => {
				resetForm();
				setIsShareQuestionerInputShownState(false);
				setIsAppointmentLoadingState(false);
				if (isFunction(onSuccess)) onSuccess();
			},
		});
	};

	const onPageLoadGetUsersHandler = useCallback(() => {
		setUsersLoadingState(true);

		if (usersCancelRequestRef.current?.signal) usersCancelRequestRef.current?.abort();
		usersCancelRequestRef.current = new AbortController();

		apiHandler(getUsers, {
			data: {
				query: { disable_pagination: true },
				options: { signal: usersCancelRequestRef.current.signal },
			},
			onError: () => setUsersLoadingState(false),
			onSuccess: ({ users: responseUsers = [] }) => {
				const users = flattenDeep([
					...(responseUsers || []),
					...(responseUsers?.map(({ assistant = [] }) => assistant) || []),
				]).filter(({ type }) => vars.app.usersTypes.officeStaff !== type);
				setUsersState(users);
				setUsersLoadingState(false);
			},
		});
	}, []);

	// formik
	const isVisitTypeFieldShown = Boolean(vars?.app?.isNoteVisitTypeShown);
	const isVirtualAssessmentEnabled = Boolean(globals?.tenant_info?.virtual_assessment);
	const isCaseTypeFieldShown = !isVirtualAssessmentEnabled;

	const { submitForm: appointmentFormikSubmitForm, ...appointmentFormik } = useFormik({
		enableReinitialize: true,
		initialValues: {
			time: "",
			type: defaultAppointmentType,
			reason: "",
			user_id: "",
			duration_minutes: defaultUserSlotDuration,
			...(appointment || {}),
			// The API and the calendar both speak d-m-Y; the picker shows yyyy-mm-dd.
			date: fromApiDate(appointment?.date),
			patient_id: patient?.id,
			patient_name: [patient?.first_name || "", patient?.last_name || ""].filter(Boolean).join(" ").trim(),
			gender: patientGender,
			age_years: patientAge.years,
			age_months: patientAge.months,
			age_days: patientAge.days,
		},
		validationSchema: object().shape({
			date: string().required(t("system_messages.required_field")),
			time: string().required(t("system_messages.required_field")),
			type: string()
				.required(t("system_messages.required_field"))
				.oneOf(APPOINTMENT_TYPES?.map((type) => type?.value).filter(Boolean)),
			reason: string()
				.optional()
				.max(255, t("system_messages.must_be_less_than", { characters: 255 }))
				.test({
					name: "not_contain_URL",
					test: (value) => !!!value || ![...(value.match(urlRegExp) || [])].length,
					message: t("system_messages.cant_contain_url"),
				})
				.test({
					name: "not_contain_phone_number",
					test: (value) => !!!value || ![...(value.match(phoneRegExp) || [])].length,
					message: t("system_messages.cant_contain_phone_numbers"),
				})
				.test({
					name: "not_contain_email",
					test: (value) => !!!value || ![...(value.match(emailRegExp) || [])].length,
					message: t("system_messages.cant_contain_an_email"),
				})
				.test({
					name: "not_contain_bad_word",
					test: (value) => !!!value || !profanity.exists(value),
					message: t("system_messages.cant_contain_bad_words"),
				})
				.test({
					name: "not_contain_ssn",
					test: (value) => !!!value || ![...(value.match(ssnRegExp) || [])].length,
					message: t("system_messages.cant_contain_social_security_numbers"),
				}),
			remarks: string()
				.optional()
				.max(255, t("system_messages.must_be_less_than", { characters: 255 }))
				.test({
					name: "not_contain_URL",
					test: (value) => !!!value || ![...(value.match(urlRegExp) || [])].length,
					message: t("system_messages.cant_contain_url"),
				})
				.test({
					name: "not_contain_phone_number",
					test: (value) => !!!value || ![...(value.match(phoneRegExp) || [])].length,
					message: t("system_messages.cant_contain_phone_numbers"),
				})
				.test({
					name: "not_contain_email",
					test: (value) => !!!value || ![...(value.match(emailRegExp) || [])].length,
					message: t("system_messages.cant_contain_an_email"),
				})
				.test({
					name: "not_contain_bad_word",
					test: (value) => !!!value || !profanity.exists(value),
					message: t("system_messages.cant_contain_bad_words"),
				})
				.test({
					name: "not_contain_ssn",
					test: (value) => !!!value || ![...(value.match(ssnRegExp) || [])].length,
					message: t("system_messages.cant_contain_social_security_numbers"),
				}),
			patient_id: string().required(t("system_messages.required_field")),
			patient_name: string(),
			gender: string().required(t("system_messages.required_field")),
			age_years: number().required(t("system_messages.required_field")),
			age_months: number().required(t("system_messages.required_field")),
			age_days: number().required(t("system_messages.required_field")),
			user_id: string().required(t("system_messages.required_field")).nullable(),
			case_id: string()
				.nullable()
				.when("isRequired", {
					is: () => isShareQuestionerInputShownState,
					then: (schema) => schema.required(t("system_messages.required_field")),
				}),
			attachments: array().nullable(),
			duration_minutes: number()
				.required(t("system_messages.required_field"))
				.max(30, "Duration can't be more than 30 min!")
				.min(10, "Duration can't be less than 10 min!"),
			visit_type: string(),
			visit_recurrence: string(),
		}),
		onSubmit: onAppointmentFormikSubmitHandler,
	});

	// effect hooks
	useEffect(() => {
		if (forceTimeOverlap && Object.keys(appointment).length) appointmentFormikSubmitForm();
	}, [appointment, appointmentFormikSubmitForm, forceTimeOverlap]);

	useEffect(() => {
		const tooltipElements = [...(formRef.current?.querySelectorAll(`[data-bs-toggle="tooltip"]`) || [])];
		tooltipElements.forEach((element) => Tooltip.getOrCreateInstance(element));
		return () => {
			tooltipElements.forEach((element) => Tooltip.getInstance(element)?.dispose());
		};
	}, [isCurrentSegmentMedicalSegment, isCurrentSegmentPharmacySegment]);

	useEffect(() => {
		if (isCurrentSegmentMedicalSegment || isCurrentSegmentPharmacySegment) onPageLoadGetUsersHandler();
	}, [isCurrentSegmentMedicalSegment, isCurrentSegmentPharmacySegment, onPageLoadGetUsersHandler]);

	useEffect(() => {
		return () => {
			if (usersCancelRequestRef.current?.signal) usersCancelRequestRef.current?.abort();
			if (postAppointmentCancelRequestRef.current?.signal) postAppointmentCancelRequestRef.current?.abort();
		};
	}, []);

	return (
		<form ref={formRef} id={`appointment-form-${uniqueId}`} onSubmit={appointmentFormik.handleSubmit} noValidate>
			<FocusError formik={{ submitForm: appointmentFormikSubmitForm, ...appointmentFormik }} />
			<fieldset disabled={isAppointmentLoadingState}>
				<legend className="visually-hidden">create appointment form:</legend>
				<div className="row gy-4">
					<div className="col-12">
						<fieldset className="row gy-2">
							<div className="col-12">
								<legend className="fs-6 text-uppercase mb-0">
									<strong>
										<small>visit date and time</small>
									</strong>
								</legend>
							</div>
							<div className="col-12">
								<div className="row gy-4">
									{!isSinglePatientView && (
										<div className="col-12 col-lg-6">
											<label
												htmlFor={`patientNameField-${uniqueId}`}
												className="form-label text-capitalize border-0 mb-0">
												<small>
													{t("fields.patient")} <span className="ms-1">*</span>
												</small>
											</label>
											<input
												value={appointmentFormik.values?.patient_name || ""}
												type="text"
												name="patient_name"
												id={`patientNameField-${uniqueId}`}
												className={`form-control form-control-sm ps-0 rounded-0 bg-transparent border-top-0 border-start-0 border-end-0 py-2 text-truncate ${
													(!!appointmentFormik.touched?.patient_name &&
														!!appointmentFormik.errors?.patient_name) ||
													(!!appointmentFormik.touched?.patient_id &&
														!!appointmentFormik.errors?.patient_id)
														? "is-invalid"
														: (appointmentFormik.values?.patient_name &&
																!!appointmentFormik.touched?.patient_name &&
																!!!appointmentFormik.errors?.patient_name) ||
														  (appointmentFormik.values?.patient_id &&
																!!appointmentFormik.touched?.patient_id &&
																!!!appointmentFormik.errors?.patient_id)
														? "is-valid"
														: ""
												}`}
												placeholder={t("fields.select_patient")}
												readOnly
												required
											/>
											<div className="form-text text-end">
												<button
													type="button"
													onClick={() =>
														isFunction(onChangePatientClickHandler) &&
														onChangePatientClickHandler()
													}
													className="btn btn-sm btn-link text-capitalize text-decoration-none border-0 p-0 rounded-0">
													<small>
														<strong>Change patient</strong>
													</small>
												</button>
											</div>
											{((!!appointmentFormik.touched?.patient_name &&
												!!appointmentFormik.errors?.patient_name) ||
												(!!appointmentFormik.touched?.patient_id &&
													!!appointmentFormik.errors?.patient_id)) && (
												<div className="invalid-feedback text-uppercase">
													<strong>
														{appointmentFormik?.errors?.patient_name ||
															appointmentFormik?.errors?.patient_id}
													</strong>
												</div>
											)}
										</div>
									)}
									<div className="col-12 col-lg-6">
										{(isCurrentSegmentMedicalSegment || isCurrentSegmentPharmacySegment) && (
											<>
												<label
													htmlFor={`userField-${uniqueId}`}
													className="form-label text-capitalize border-0 mb-0">
													<small>
														Select Team Member <span className="ms-1">*</span>
													</small>
												</label>
												<select
													onChange={appointmentFormik.handleChange}
													onBlur={appointmentFormik.handleBlur}
													value={appointmentFormik.values?.user_id || ""}
													name="user_id"
													className={`form-select form-select-sm rounded-0 border-top-0 border-start-0 border-end-0 text-truncate ${
														!!appointmentFormik.touched?.user_id &&
														!!appointmentFormik.errors?.user_id
															? "is-invalid"
															: appointmentFormik.values?.user_id &&
															  !!appointmentFormik.touched?.user_id &&
															  !!!appointmentFormik.errors?.user_id
															? "is-valid"
															: ""
													}`}
													disabled={usersLoadingState}
													id={`userField-${uniqueId}`}
													placeholder=""
													required>
													<option value="" disabled>
														Select Team Member
													</option>
													{usersState?.map(({ full_name, id }) => (
														<option value={id} key={id}>
															{id !== user?.id
																? full_name
																: `${
																		user?.full_name || user?.first_name || "User"
																  } - You`}
														</option>
													))}
												</select>
												{!!appointmentFormik.touched?.user_id &&
													!!appointmentFormik.errors?.user_id && (
														<div className="invalid-feedback text-uppercase">
															<strong>{appointmentFormik.errors.user_id}</strong>
														</div>
													)}
											</>
										)}
									</div>
									<div className="col-12 col-lg-6">
										<Flatpickr
											type="text"
											name="date"
											placeholder={t("fields.select_date")}
											id={`appointmentDateField-${uniqueId}`}
											className={`form-select form-select-sm ps-0 border-start-0 border-end-0 border-top-0 hide-clear text-truncate bg-transparent py-2 flatpickr-input ${
												!!appointmentFormik?.touched?.date && !!appointmentFormik.errors?.date
													? "is-invalid"
													: appointmentFormik.values?.date &&
													  !!appointmentFormik?.touched?.date &&
													  !!!appointmentFormik.errors?.date
													? "is-valid"
													: ""
											}`}
											disabled={isAppointmentLoadingState}
											onOpen={() => appointmentFormik.setFieldTouched("date", true, false)}
											onClose={() => appointmentFormik.setFieldTouched("date", true)}
											onChange={(_, dateStr) => appointmentFormik.setFieldValue("date", dateStr)}
											value={appointmentFormik.values?.date || ""}
											options={{
												...(appointmentFormik.values?.date && {
													defaultDate: appointmentFormik.values.date,
												}),
												// Show (and hold in formik) yyyy-mm-dd, the format the
												// helper text below promises. altInput would render a
												// second, duplicate field here, so the d-m-Y the API
												// wants is produced by toApiDate() on submit instead.
												dateFormat: "Y-m-d",
												static: true,
												allowInput: true,
											}}
											autoComplete="off"
											readOnly
											render={({ onChange, ...props }, ref) => (
												<>
													<label
														htmlFor={`appointmentDateField-${uniqueId}`}
														className="text-capitalize border-0">
														<small>
															{t("fields.select_date")} <span className="ms-1">*</span>
														</small>
													</label>
													<input {...props} onChange={onChange} ref={ref} />
													<span className="form-text small mt-0 lh-1 d-block text-start">
														<small>yyyy-mm-dd</small>
													</span>
													{!!appointmentFormik?.touched?.date &&
														!!appointmentFormik.errors?.date && (
															<div className="invalid-feedback text-uppercase">
																<strong>{appointmentFormik.errors.date}</strong>
															</div>
														)}
												</>
											)}
										/>
									</div>
									<div className="col-12 col-sm-6 col-lg-3">
										<Flatpickr
											type="text"
											name="time"
											placeholder={t("fields.select_time")}
											id={`appointmentTimeField-${uniqueId}`}
											className={`form-select form-select-sm ps-0 border-start-0 border-end-0 border-top-0 hide-clear text-truncate bg-transparent py-2 flatpickr-input ${
												!!appointmentFormik?.touched?.time && !!appointmentFormik.errors?.time
													? "is-invalid"
													: appointmentFormik.values?.time &&
													  !!appointmentFormik?.touched?.time &&
													  !!!appointmentFormik.errors?.time
													? "is-valid"
													: ""
											}`}
											disabled={isAppointmentLoadingState}
											onOpen={() => appointmentFormik.setFieldTouched("time", true, false)}
											onClose={() => appointmentFormik.setFieldTouched("time", true)}
											onChange={(_, timeStr) =>
												appointmentFormik.setFieldValue(
													"time",
													moment(timeStr, "h:mm A").format("hh:mm A")
												)
											}
											value={appointmentFormik.values?.time || ""}
											options={{
												enableTime: true,
												noCalendar: true,
												allowInput: true,
												dateFormat: "G:i K",
												...((appointmentFormik.values?.time && {
													defaultDate: appointmentFormik.values?.time,
												}) ||
													{}),
											}}
											autoComplete="off"
											readOnly
											render={({ onChange, ...props }, ref) => (
												<>
													<label
														htmlFor={`appointmentTimeField-${uniqueId}`}
														className="text-capitalize border-0">
														<small>
															{t("fields.select_time")} <span className="ms-1">*</span>
														</small>
													</label>
													<input {...props} onChange={onChange} ref={ref} />
													{!!appointmentFormik?.touched?.time &&
														!!appointmentFormik.errors?.time && (
															<div className="invalid-feedback text-uppercase">
																<strong>{appointmentFormik.errors.time}</strong>
															</div>
														)}
												</>
											)}
										/>
									</div>
									<div className="col-12 col-sm-6 col-lg-3">
										<label
											htmlFor={`durationMinutesField-${uniqueId}`}
											className="form-label text-capitalize border-0 mb-0">
											<small>
												Add Duration (mins) <span className="ms-1">*</span>
											</small>
										</label>
										<select
											onChange={appointmentFormik.handleChange}
											onBlur={appointmentFormik.handleBlur}
											value={appointmentFormik.values?.duration_minutes || ""}
											name="duration_minutes"
											className={`form-select form-select-sm rounded-0 border-top-0 border-start-0 border-end-0 text-truncate ${
												!!appointmentFormik.touched?.duration_minutes &&
												!!appointmentFormik.errors?.duration_minutes
													? "is-invalid"
													: appointmentFormik.values?.duration_minutes &&
													  !!appointmentFormik.touched?.duration_minutes &&
													  !!!appointmentFormik.errors?.duration_minutes
													? "is-valid"
													: ""
											}`}
											id={`durationMinutesField-${uniqueId}`}
											placeholder="Duration: 15min"
											required>
											<option value="" disabled>
												-Select Duration-
											</option>
											{[
												defaultUserSlotDuration,
												defaultUserSlotDuration + 5,
												defaultUserSlotDuration + 10,
											]?.map((number) => (
												<option value={number} key={number}>
													{number}
												</option>
											))}
										</select>
										{!!appointmentFormik.touched?.duration_minutes &&
											!!appointmentFormik.errors?.duration_minutes && (
												<div className="invalid-feedback text-uppercase">
													<strong>{appointmentFormik.errors.duration_minutes}</strong>
												</div>
											)}
									</div>
								</div>
							</div>
						</fieldset>
					</div>
					<div className="col-12">
						<fieldset className="row gy-2">
							<div className="col-12">
								<legend className="fs-6 text-uppercase mb-0">
									<strong>
										<small>appointment details</small>
									</strong>
								</legend>
							</div>
							<div className="col-12">
								<div className="row gy-4">
									<div className="col-12">
										<div className="row gy-4">
											{isVisitTypeFieldShown && (
												<div className="col-12 col-lg-6">
													<div className="row row-cols-2">
														<div className="col-12">
															<label
																htmlFor={`visitTypeField0-${uniqueId}`}
																className="form-label text-capitalize border-0 mb-0">
																<small>Choose your visit type</small>
															</label>
														</div>
														{[...(vars?.app?.noteVisitType || [])].map(
															(type, typeIndex, typeRow) => (
																<div className="col" key={type.label}>
																	<div className="form-check">
																		<input
																			onChange={appointmentFormik?.handleChange}
																			onBlur={appointmentFormik?.handleBlur}
																			type="radio"
																			className={`form-check-input ${
																				!!appointmentFormik?.touched
																					?.visit_type &&
																				!!appointmentFormik?.errors?.visit_type
																					? "is-invalid"
																					: appointmentFormik?.values
																							?.visit_type &&
																					  !!appointmentFormik?.touched
																							?.visit_type &&
																					  !!!appointmentFormik?.errors
																							?.visit_type
																					? "is-valid"
																					: ""
																			}`}
																			value={type.value}
																			name="visit_type"
																			id={`visitTypeField${typeIndex}-${uniqueId}`}
																			autoComplete="off"
																			checked={
																				appointmentFormik.values?.visit_type ===
																					type.value || false
																			}
																		/>
																		<label
																			className="form-check-label text-capitalize"
																			htmlFor={`visitTypeField${typeIndex}-${uniqueId}`}>
																			<small>{type.label}</small>
																		</label>
																		{typeIndex >= typeRow?.length - 1 &&
																			!!appointmentFormik.touched?.visit_type &&
																			!!appointmentFormik.errors?.visit_type && (
																				<>
																					<input
																						value={
																							appointmentFormik.values
																								?.visit_type || ""
																						}
																						type="text"
																						name="visit_type"
																						className={`form-control rounded-0 border-top-0 border-start-0 border-end-0 text-truncate ${
																							!!appointmentFormik.touched
																								?.visit_type &&
																							!!appointmentFormik.errors
																								?.visit_type
																								? "is-invalid"
																								: appointmentFormik
																										.values
																										?.visit_type &&
																								  !!appointmentFormik
																										.touched
																										?.visit_type &&
																								  !!!appointmentFormik
																										.errors
																										?.visit_type
																								? "is-valid"
																								: ""
																						}`}
																						readOnly
																						hidden
																						aria-hidden="true"
																					/>
																					<div className="invalid-tooltip text-uppercase start-0">
																						<strong>
																							{
																								appointmentFormik.errors
																									.visit_type
																							}
																						</strong>
																					</div>
																				</>
																			)}
																	</div>
																</div>
															)
														)}
													</div>
												</div>
											)}
											{isCaseTypeFieldShown && (
												<div className={`col-12 ${isVisitTypeFieldShown ? "col-lg-6" : ""}`}>
													<div className="row row-cols-2">
														<div className="col-12">
															<label
																htmlFor={`visitRecurrenceField0-${uniqueId}`}
																className="form-label text-capitalize border-0 mb-0">
																<small>Choose your case type</small>
															</label>
														</div>
														{[...(vars?.app?.noteVisitRecurrence || [])].map(
															(
																visitRecurrence,
																visitRecurrenceIndex,
																visitRecurrenceRow
															) => (
																<div className="col" key={visitRecurrence.label}>
																	<div className="form-check">
																		<input
																			onChange={appointmentFormik?.handleChange}
																			onBlur={appointmentFormik?.handleBlur}
																			type="radio"
																			className={`form-check-input ${
																				!!appointmentFormik?.touched
																					?.visit_recurrence &&
																				!!appointmentFormik?.errors
																					?.visit_recurrence
																					? "is-invalid"
																					: appointmentFormik?.values
																							?.visit_recurrence &&
																					  !!appointmentFormik?.touched
																							?.visit_recurrence &&
																					  !!!appointmentFormik?.errors
																							?.visit_recurrence
																					? "is-valid"
																					: ""
																			}`}
																			value={visitRecurrence.value}
																			name="visit_recurrence"
																			id={`visitRecurrenceField${visitRecurrenceIndex}-${uniqueId}`}
																			autoComplete="off"
																			checked={
																				appointmentFormik.values
																					?.visit_recurrence ===
																					visitRecurrence.value || false
																			}
																		/>
																		<label
																			className="form-check-label text-capitalize"
																			htmlFor={`visitRecurrenceField${visitRecurrenceIndex}-${uniqueId}`}>
																			<small>{`${visitRecurrence.label} Case`}</small>
																		</label>
																		{visitRecurrenceIndex >=
																			visitRecurrenceRow?.length - 1 &&
																			!!appointmentFormik.touched
																				?.visit_recurrence &&
																			!!appointmentFormik.errors
																				?.visit_recurrence && (
																				<>
																					<input
																						value={
																							appointmentFormik.values
																								?.visit_recurrence || ""
																						}
																						type="text"
																						name="visit_recurrence"
																						className={`form-control rounded-0 border-top-0 border-start-0 border-end-0 text-truncate ${
																							!!appointmentFormik.touched
																								?.visit_recurrence &&
																							!!appointmentFormik.errors
																								?.visit_recurrence
																								? "is-invalid"
																								: appointmentFormik
																										.values
																										?.visit_recurrence &&
																								  !!appointmentFormik
																										.touched
																										?.visit_recurrence &&
																								  !!!appointmentFormik
																										.errors
																										?.visit_recurrence
																								? "is-valid"
																								: ""
																						}`}
																						readOnly
																						hidden
																						aria-hidden="true"
																					/>
																					<div className="invalid-tooltip text-uppercase start-0">
																						<strong>
																							{
																								appointmentFormik.errors
																									.visit_recurrence
																							}
																						</strong>
																					</div>
																				</>
																			)}
																	</div>
																</div>
															)
														)}
													</div>
												</div>
											)}
										</div>
									</div>
									{(isCurrentSegmentMedicalSegment || isCurrentSegmentPharmacySegment) && (
										<div className="col-12">
											<div className="row gy-4">
												<div className="col-12 col-lg-6">
													<div className="form-check">
														<input
															className="form-check-input"
															type="checkbox"
															onChange={({ target: { checked } }) => {
																appointmentFormik.setFieldValue("case_id", null);
																appointmentFormik.setFieldTouched("case_id", checked);
																setIsShareQuestionerInputShownState(checked);
															}}
															id={`shareQuestionerToggleField-${uniqueId}`}
															autoComplete="off"
															checked={isShareQuestionerInputShownState}
														/>
														<label
															className="form-check-label text-capitalize"
															htmlFor={`shareQuestionerToggleField-${uniqueId}`}>
															<span id={`shareQuestionerToggleFieldLabel-${uniqueId}`}>
																<small>Share Questionnaire with patient</small>
															</span>
															<button
																className="btn btn-link link-dark p-0 rounded-circle border-0 ms-1 lh-1 op-20"
																data-bs-toggle="tooltip"
																data-bs-placement="top"
																data-bs-title="Share assessment questions with the patient to answer before the actual appointment.">
																<svg
																	xmlns="http://www.w3.org/2000/svg"
																	width="1em"
																	height="1em"
																	fill="currentColor"
																	viewBox="0 0 16 16">
																	<path d="M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16m.93-9.412-1 4.705c-.07.34.029.533.304.533.194 0 .487-.07.686-.246l-.088.416c-.287.346-.92.598-1.465.598-.703 0-1.002-.422-.808-1.319l.738-3.468c.064-.293.006-.399-.287-.47l-.451-.081.082-.381 2.29-.287zM8 5.5a1 1 0 1 1 0-2 1 1 0 0 1 0 2" />
																</svg>
															</button>
														</label>
													</div>
												</div>
												<div className="col-12 col-lg-6">
													{isShareQuestionerInputShownState && (
														<CasesSearchInput form={appointmentFormik} />
													)}
												</div>
											</div>
										</div>
									)}
									<div className="col-12 col-lg-6">
										<div className="row gy-4">
											{/* Hidden appointment type field with default value */}
											<input
												type="hidden"
												name="type"
												value={appointmentFormik.values?.type || ""}
											/>
											<div className="col-12">
												<label
													htmlFor={`reasonTextField-${uniqueId}`}
													className="form-label text-capitalize border-0 mb-0">
													<small>{t("fields.reason")}</small>
												</label>
												<textarea
													onChange={appointmentFormik.handleChange}
													onBlur={appointmentFormik.handleBlur}
													value={appointmentFormik?.values?.reason || ""}
													name="reason"
													id={`reasonTextField-${uniqueId}`}
													className={`form-control form-control-sm ps-0 rounded-0 bg-transparent border-top-0 border-start-0 border-end-0 text-truncate customized-scroll ${
														!!appointmentFormik?.touched?.reason &&
														!!appointmentFormik?.errors?.reason
															? "is-invalid"
															: appointmentFormik?.values?.reason &&
															  !!appointmentFormik?.touched?.reason &&
															  !!!appointmentFormik?.errors?.reason
															? "is-valid"
															: ""
													}`}
													placeholder="Add Reason..."
													aria-describedby="reasonHelp"></textarea>
												<div
													id={`reasonHelp-${uniqueId}`}
													className="form-text text-end text-uppercase float-end position-relative bg-white mt-n4"
													style={{ zIndex: "10" }}>
													{`${appointmentFormik?.values?.reason?.length || 0}/255`}
												</div>
												{!!appointmentFormik?.touched?.reason &&
													!!appointmentFormik?.errors?.reason && (
														<div className="invalid-feedback text-uppercase">
															<strong>{appointmentFormik.errors.reason}</strong>
														</div>
													)}
											</div>
										</div>
									</div>
									<div className="col-12 col-lg-6">
										<div className="row gy-4">
											<div className="col-12">
												<label
													htmlFor={`remarksTextField-${uniqueId}`}
													className="form-label text-capitalize border-0 mb-0">
													<small>{t("fields.remarks")}</small>
												</label>
												<textarea
													onChange={appointmentFormik.handleChange}
													onBlur={appointmentFormik.handleBlur}
													value={appointmentFormik?.values?.remarks || ""}
													name="remarks"
													id={`remarksTextField-${uniqueId}`}
													className={`form-control form-control-sm ps-0 rounded-0 bg-transparent border-top-0 border-start-0 border-end-0 text-truncate customized-scroll ${
														!!appointmentFormik?.touched?.remarks &&
														!!appointmentFormik?.errors?.remarks
															? "is-invalid"
															: appointmentFormik?.values?.remarks &&
															  !!appointmentFormik?.touched?.remarks &&
															  !!!appointmentFormik?.errors?.remarks
															? "is-valid"
															: ""
													}`}
													placeholder="Add Remarks..."
													aria-describedby="remarksHelp"
													style={{ height: "138px" }}
													required></textarea>
												<div
													id={`remarksHelp-${uniqueId}`}
													className="form-text text-end text-uppercase float-end position-relative bg-white mt-n4"
													style={{ zIndex: "10" }}>
													{`${appointmentFormik?.values?.remarks?.length || 0}/255`}
												</div>
												{!!appointmentFormik?.touched?.remarks &&
													!!appointmentFormik?.errors?.remarks && (
														<div className="invalid-feedback text-uppercase">
															<strong>{appointmentFormik.errors.remarks}</strong>
														</div>
													)}
											</div>
										</div>
									</div>
									<div className="col-12">
										<div className="row gy-4">
											<div className="col-12 col-lg-6 position-relative">
												<DocumentsSearchInput
													form={appointmentFormik}
													patient={patient}
													selectedDocuments={selectedDocuments}
													setSelectedDocuments={setSelectedDocuments}
													shouldRefetch={refetchDocuments}
													setShouldRefetch={setRefetchDocuments}
												/>
											</div>
											<div className="col-12 col-lg-6">
												<button
													type="button"
													onClick={() => onUploadNewDocumentClickHandler()}
													className="btn btn-sm btn-link p-1 border-0 text-decoration-none text-uppercase text-start w-100 lh-1 w-100">
													<span className="d-flex align-items-center justify-content-center gap-2">
														<span className="btn-icon flex-shrink-0">
															<svg
																xmlns="http://www.w3.org/2000/svg"
																width="27"
																height="25"
																viewBox="0 0 22 11"
																fill="currentColor"
																className="rotate-n45 w-27px h-25px">
																<path
																	d="M18.5,16H7A4,4,0,0,1,7,8H19.5a2.5,2.5,0,0,1,0,5H9a1,1,0,0,1,0-2h9.5V9.5H9a2.5,2.5,0,0,0,0,5H19.5a4,4,0,0,0,0-8H7a5.5,5.5,0,0,0,0,11H18.5Z"
																	transform="translate(-1.5 -6.5)"
																/>
															</svg>
														</span>
														<span className="btn-text flex-grow-1 d-flex flex-column">
															<strong>Upload new document</strong>
															<em className="text-secondary">
																<small>PDF, Maximum size 5MB</small>
															</em>
														</span>
													</span>
												</button>
											</div>
										</div>
									</div>
								</div>
							</div>
						</fieldset>
					</div>
					<div className="col-12 mt-5">
						<div className="row gy-4">
							{isEdit && (
								<div className="col-12 col-lg-6">
									<button
										type="button"
										className="btn btn-outline-danger w-100 text-uppercase"
										onClick={() =>
											isFunction(onDeleteButtonClickHandler) && onDeleteButtonClickHandler()
										}>
										<strong>
											<small>{t("actions.delete")}</small>
										</strong>
									</button>
								</div>
							)}
							<div className="col-12 col-lg">
								<button type="submit" className="btn btn-primary w-100 text-uppercase">
									<strong>
										<small>{isEdit ? t("actions.update") : t("actions.schedule")}</small>
									</strong>
									{isAppointmentLoadingState && (
										<span className="spinner-border spinner-border-sm ms-2" role="status">
											<span className="visually-hidden">{`${t("content.loading")}...`}</span>
										</span>
									)}
								</button>
							</div>
						</div>
					</div>
				</div>
			</fieldset>
		</form>
	);
};

export default Appointment;
